import { useDisclosure } from '@mantine/hooks';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ActionBar,
  Button,
  ButtonValid,
  CardV2 as Card,
  Form,
  Modal,
  toast,
  Toggle,
} from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import { useAccount } from '@feathr/extender/state';
import { flattenErrors, getIconForAction } from '@feathr/hooks';
import type { TValidateGrouped } from '@feathr/rachis';

interface IErrors extends TValidateGrouped {
  settings: {
    allow_early_access?: string[];
  };
  ttd: {
    allow_cross_device?: string[];
  };
}

function GeneralSettingsPage(): JSX.Element {
  const account = useAccount();
  const [opened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const { t } = useTranslation();

  const hasEarlyAccess = account.getSetting('allow_early_access');
  const hasBeeFreeDevMode = account.getSetting('beefree_dev_mode') ?? false;

  const [isDirtyEarlyAccess, isDirtyBeeFreeDevMode] = ((): [boolean, boolean] => {
    const initalSettings = account.shadowAttributes.settings ?? {};
    // If toggle was used but unset, value will sometimes be false instead of undefined, so normalize the checking
    const isDirtyEarlyAccess = !!(
      account.getSetting('allow_early_access') !== initalSettings.allow_early_access
    );
    const isDirtyBeeFreeDevMode = !!(
      account.getSetting('beefree_dev_mode') !== initalSettings.beefree_dev_mode
    );
    return [isDirtyEarlyAccess, isDirtyBeeFreeDevMode];
  })();

  useEffect(() => {
    // Reset changed account settings if they weren't saved so
    // the user doesn't see the changes if they navigate away
    // and back to this page.
    return () => {
      if (account.isDirty) {
        account.discardDirty();
      }
    };
  }, [account]);

  async function handleSave(): Promise<void> {
    if (isDirtyEarlyAccess) {
      openModal();
    } else {
      await save();
    }
  }

  function handleEarlyAccessChange(newValue?: boolean): void {
    account.setSetting('allow_early_access', newValue);
    account.set({
      ttd: {
        ...account.get('ttd'),
        allow_cross_device: newValue,
      },
    });
  }

  async function save(): Promise<void> {
    try {
      await account.patchDirty();

      if (isDirtyEarlyAccess) {
        toast(
          hasEarlyAccess
            ? t('Enabled early access to features in beta')
            : t('Disabled early access to features in beta'),
          {
            type: 'success',
          },
        );
      }
      if (isDirtyBeeFreeDevMode) {
        // Wait for early access toast to render so both will be visible
        if (isDirtyEarlyAccess) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
        toast(
          hasBeeFreeDevMode
            ? t('BeeFree editor is in development mode')
            : t('BeeFree editor is in production mode'),
          {
            type: 'success',
          },
        );
      }
    } catch (error) {
      toast(t('There was an error saving your settings. {{- error}}', { error }), {
        type: 'error',
      });
    } finally {
      closeModal();
    }
  }

  async function handleBeeFreeDevModeChange(newValue?: boolean): Promise<void> {
    account.setSetting('beefree_dev_mode', newValue ?? false);
  }

  const errors: IErrors = account.validate<IErrors>(
    ['settings.allow_early_access', 'ttd.allow_cross_device'],
    false,
    'grouped',
  ).errors;

  return (
    <Page title={t('General')}>
      <Form label={t('Account general settings')}>
        <Card>
          <Card.Header title={t('Early Access')} />
          <Card.Content>
            <Toggle
              label={t('Get early access to features in beta')}
              layout={'well'}
              onChange={handleEarlyAccessChange}
              value={hasEarlyAccess}
            />
          </Card.Content>
        </Card>
        {/* TODO: This toggle should be moved to the Profile page that has other sudo related settings. General is a user facing page 
        that should not need to be aware of sudo or is_internal. */}
        {account.isInternal && (
          <Card>
            <Card.Header title={t('BeeFree Editor')} />
            <Card.Content
              addVerticalGap={true}
              description={t(
                'Switch to development mode to debug and test features. This is only available for internal Feathr accounts.',
              )}
            >
              <Toggle
                label={t('Development mode')}
                layout={'well'}
                name={'beefree-dev-mode'}
                onChange={handleBeeFreeDevModeChange}
                value={hasBeeFreeDevMode}
              />
            </Card.Content>
          </Card>
        )}
      </Form>
      <Modal
        description={t(
          'This will affect how important Feathr features like campaigns and reports perform.',
        )}
        onClose={closeModal}
        opened={opened}
        rightActions={
          <>
            <Button onClick={closeModal}>{t('Cancel')}</Button>
            <Button onClick={save} type={hasEarlyAccess ? 'primary' : 'danger'}>
              {hasEarlyAccess ? t('Enable') : t('Disable')}
            </Button>
          </>
        }
        title={
          hasEarlyAccess
            ? t('Enable early access to features in beta')
            : t('Disable early access to features in beta')
        }
      />

      <ActionBar
        right={
          <ButtonValid
            disabled={!account.isDirty || undefined}
            errors={flattenErrors(errors)}
            onClick={handleSave}
            prefix={getIconForAction('save')}
          >
            {t('Save')}
          </ButtonValid>
        }
        usePortal={true}
      />
    </Page>
  );
}

export default observer(GeneralSettingsPage);
